import { http } from './request'
import { ApiError, getApiMessage, isApiResponse } from './types'

export const OVERSEAS_DECRYPT_EXPORT_BATCH_DEFAULT = 50
export const OVERSEAS_DECRYPT_EXPORT_BATCH_MIN = 1
export const OVERSEAS_DECRYPT_EXPORT_BATCH_MAX = 500

export interface OverseasExportJobStart {
  jobId: string
  total: number
  decryptSensitive: boolean
  batchSize: number
}

export interface OverseasExportJobStatus {
  jobId: string
  status: 'QUEUED' | 'DECRYPTING' | 'WRITING' | 'SUCCESS' | 'FAILED' | string
  stageLabel: string
  percent: number
  processed: number
  total: number
  cacheHitFields: number
  icspFields: number
  icspCalls: number
  decryptSensitive: boolean
  batchSize: number
  error?: string
  suggestion?: string
  fileReady: boolean
}

export function asJobStart(raw: unknown): OverseasExportJobStart {
  const o = (raw ?? {}) as Record<string, unknown>
  return {
    jobId: String(o.jobId ?? ''),
    total: Number(o.total ?? 0),
    decryptSensitive: o.decryptSensitive === true,
    batchSize: Number(o.batchSize ?? OVERSEAS_DECRYPT_EXPORT_BATCH_DEFAULT),
  }
}

export function asJobStatus(raw: unknown): OverseasExportJobStatus {
  const o = (raw ?? {}) as Record<string, unknown>
  return {
    jobId: String(o.jobId ?? ''),
    status: String(o.status ?? ''),
    stageLabel: String(o.stageLabel ?? ''),
    percent: Number(o.percent ?? 0),
    processed: Number(o.processed ?? 0),
    total: Number(o.total ?? 0),
    cacheHitFields: Number(o.cacheHitFields ?? 0),
    icspFields: Number(o.icspFields ?? 0),
    icspCalls: Number(o.icspCalls ?? 0),
    decryptSensitive: o.decryptSensitive === true,
    batchSize: Number(o.batchSize ?? OVERSEAS_DECRYPT_EXPORT_BATCH_DEFAULT),
    error: typeof o.error === 'string' ? o.error : undefined,
    suggestion: typeof o.suggestion === 'string' ? o.suggestion : undefined,
    fileReady: o.fileReady === true,
  }
}

/** 任务文件可能是 Excel，也可能是业务失败 JSON（HTTP 仍 200） */
export async function downloadExportJobExcel(url: string, filename: string): Promise<void> {
  const blob = await http.getBlob(url, { timeout: 120000, skipErrorMessage: true })
  const type = (blob.type || '').toLowerCase()
  const looksJson = type.includes('json') || type.includes('text')
  if (looksJson || blob.size < 800) {
    const head = await blob.slice(0, 120).text()
    if (head.trim().startsWith('{')) {
      throw jsonBlobToError(await blob.text())
    }
  }
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(link.href)
}

function jsonBlobToError(text: string): ApiError {
  try {
    const parsed = JSON.parse(text) as unknown
    const msg = isApiResponse(parsed) ? getApiMessage(parsed) : '下载失败'
    const code = isApiResponse(parsed) ? parsed.code : undefined
    return new ApiError(msg || '下载失败', typeof code === 'number' ? code : undefined)
  } catch {
    return new ApiError('下载失败，请重新导出')
  }
}
